import * as React from "react";
import { RightOutlined, HomeOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { BrowserBreadcrumbProps, BreadcrumbItem } from "../types";
import styles from "../BrowserServiceModal.module.scss";

export const BrowserBreadcrumb: React.FC<BrowserBreadcrumbProps> = ({ items, onNavigate }) => {
  const maxVisibleItems = 4;

  // Calculate which items to show
  const visibleItems = React.useMemo(() => {
    if (items.length <= maxVisibleItems) {
      return items.map((item, index) => ({ item, originalIndex: index, isEllipsis: false }));
    }

    // Show first item, ellipsis, and last (maxVisibleItems - 2) items
    const result: { item: BreadcrumbItem | null; originalIndex: number; isEllipsis: boolean }[] = [];

    // First item (Home/Service)
    result.push({ item: items[0], originalIndex: 0, isEllipsis: false });

    // Ellipsis for hidden items
    const hiddenItems = items.slice(1, items.length - (maxVisibleItems - 2));
    result.push({
      item: null,
      originalIndex: -1,
      isEllipsis: true,
    });

    // Last items
    const lastItems = items.slice(items.length - (maxVisibleItems - 2));
    lastItems.forEach((item, idx) => {
      result.push({
        item,
        originalIndex: items.length - (maxVisibleItems - 2) + idx,
        isEllipsis: false
      });
    });

    return result;
  }, [items]);

  if (items.length === 0) {
    return (
      <nav aria-label="Breadcrumb" className={styles.breadcrumb}>
        <div className={styles.breadcrumbItem}>
          <HomeOutlined />
          <span>Home</span>
        </div>
      </nav>
    );
  }

  return (
    <nav aria-label="Breadcrumb" className={styles.breadcrumb}>
      <ol className={styles.breadcrumbList}>
        {visibleItems.map((entry, displayIndex) => {
          if (entry.isEllipsis) {
            return (
              <li key="ellipsis" className={styles.breadcrumbListItem}>
                <span className={styles.breadcrumbSeparator}>
                  <RightOutlined style={{ fontSize: 12 }} />
                </span>
                <span className={styles.breadcrumbEllipsis}>...</span>
              </li>
            );
          }

          const isLast = displayIndex === visibleItems.length - 1;
          const isFirst = displayIndex === 0;

          return (
            <li key={entry.item!.href} className={styles.breadcrumbListItem}>
              {!isFirst && (
                <span className={styles.breadcrumbSeparator}>
                  <RightOutlined style={{ fontSize: 12 }} />
                </span>
              )}
              {isLast ? (
                <span
                  className={`${styles.breadcrumbItem} ${styles.breadcrumbItemActive}`}
                  aria-current="page"
                >
                  {isFirst && <HomeOutlined className={styles.breadcrumbIcon} />}
                  <span className={styles.breadcrumbLabel}>{entry.item!.label}</span>
                </span>
              ) : (
                <Tooltip title={entry.item!.label}>
                  <button
                    type="button"
                    className={styles.breadcrumbItem}
                    onClick={() => onNavigate(entry.originalIndex)}
                  >
                    {isFirst && <HomeOutlined className={styles.breadcrumbIcon} />}
                    <span className={styles.breadcrumbLabel}>{entry.item!.label}</span>
                  </button>
                </Tooltip>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
